/**
 * Büroorganisation - Aufgaben für Büro, Verwaltung, interne Arbeit
 * Nicht klientenbezogene Aufgaben und Dokumente
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Briefcase, Plus, FileText, CheckCircle, Clock, AlertTriangle,
  Calendar, Loader2, Trash2, Edit, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { betreuungAPI } from '@/api/betreuungAPI';
import { toast } from 'sonner';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function BueroorganisationPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [aufgaben, setAufgaben] = useState([]);
  const [dokumente, setDokumente] = useState([]);
  const [search, setSearch] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Aufgaben mit klient_id = 'bueroorganisation' oder null/leer
      const [auf, dok] = await Promise.all([
        betreuungAPI.listAufgaben({ kategorie: 'bueroorganisation' }),
        betreuungAPI.listDokumente({ klient_id: 'bueroorganisation' })
      ]);
      // Auch Aufgaben ohne Klient anzeigen
      const alleAufgaben = await betreuungAPI.listAufgaben();
      const bueroAufgaben = alleAufgaben.filter(a => 
        a.kategorie === 'bueroorganisation' || 
        a.klient_id === 'bueroorganisation' ||
        (!a.klient_id && a.titel?.toLowerCase().includes('büro'))
      );
      setAufgaben(bueroAufgaben);
      setDokumente(Array.isArray(dok) ? dok : []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDeleteAufgabe = async (id) => {
    if (!window.confirm('Aufgabe löschen?')) return;
    try {
      await betreuungAPI.deleteAufgabe(id);
      toast.success('Gelöscht');
      loadData();
    } catch { toast.error('Fehler'); }
  };

  const getPrioColor = (prio) => {
    switch (prio) {
      case 'dringend': return 'bg-red-100 text-red-700';
      case 'hoch': return 'bg-orange-100 text-orange-700';
      case 'mittel': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const offeneAufgaben = aufgaben.filter(a => a.status === 'offen' || a.status === 'in_bearbeitung');
  const erledigteAufgaben = aufgaben.filter(a => a.status === 'erledigt');

  return (
    <div className="space-y-6" data-testid="bueroorganisation-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Briefcase className="w-6 h-6" />
            Büroorganisation
          </h1>
          <p className="text-slate-500 text-sm">Interne Aufgaben & Verwaltung</p>
        </div>
        <Button onClick={() => navigate('/aufgaben/neu?kategorie=bueroorganisation')}>
          <Plus className="w-4 h-4 mr-2" /> Neue Aufgabe
        </Button>
      </div>

      {/* Statistik */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 text-center">
            <p className="text-3xl font-bold text-blue-600">{offeneAufgaben.length}</p>
            <p className="text-sm text-slate-500">Offen</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <p className="text-3xl font-bold text-green-600">{erledigteAufgaben.length}</p>
            <p className="text-sm text-slate-500">Erledigt</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <p className="text-3xl font-bold text-amber-600">{dokumente.length}</p>
            <p className="text-sm text-slate-500">Dokumente</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <p className="text-3xl font-bold text-red-600">
              {offeneAufgaben.filter(a => a.prioritaet === 'dringend').length}
            </p>
            <p className="text-sm text-slate-500">Dringend</p>
          </CardContent>
        </Card>
      </div>

      {/* Aufgaben */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CheckCircle className="w-5 h-5" /> Aufgaben
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : offeneAufgaben.length > 0 ? (
            <div className="space-y-2">
              {offeneAufgaben.map(auf => (
                <div key={auf.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 group">
                  <div className={`w-2 h-2 rounded-full ${
                    auf.prioritaet === 'dringend' ? 'bg-red-500' :
                    auf.prioritaet === 'hoch' ? 'bg-orange-500' : 'bg-slate-400'
                  }`} />
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/aufgaben/${auf.id}`)}>
                    <p className="font-medium text-slate-800 truncate">{auf.titel}</p>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      {auf.faellig_am && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(auf.faellig_am).toLocaleDateString('de-DE')}
                        </span>
                      )}
                      {auf.prioritaet && (
                        <Badge className={`text-xs ${getPrioColor(auf.prioritaet)}`}>
                          {auf.prioritaet}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button size="sm" variant="ghost" onClick={() => navigate(`/aufgaben/${auf.id}`)}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteAufgabe(auf.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <Briefcase className="w-10 h-10 mx-auto mb-2" />
              <p>Keine offenen Büro-Aufgaben</p>
              <Button variant="outline" className="mt-4" onClick={() => navigate('/aufgaben/neu?kategorie=bueroorganisation')}>
                Erste Aufgabe anlegen
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dokumente */}
      {dokumente.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" /> Büro-Dokumente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2">
              {dokumente.map(dok => (
                <div key={dok.id} className="flex items-center gap-3 p-2 bg-slate-50 rounded hover:bg-slate-100">
                  <FileText className="w-4 h-4 text-blue-500" />
                  <span className="flex-1 truncate text-sm">{dok.dateiname || dok.titel}</span>
                  {dok.datei_url && (
                    <Button size="sm" variant="ghost" onClick={() => window.open(`${BACKEND_URL}${dok.datei_url}`, '_blank')}>
                      <Eye className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
